import { combineLatest, map, Subscription } from "rxjs";
import { DBFactory } from "~/classes/database/db_factory";

export const useUserChecklists = (userId: string) =>
  defineStore(`userChecklists/${userId}`, () => {
    const checklists = ref<Checklist[]>([]);
    const checklistSubscription = ref<Subscription | undefined>();

    const appUserData = useAppUserDataStore();
    const { isOnlineInstructor, isUserAdmin } = storeToRefs(appUserData);

    const levelsStore = useLevels();
    const { allLevels, allUserLevels } = storeToRefs(levelsStore);

    const categoriesStore = useIEWProductCategories();
    const { allUserIEWProductCategories, allIEWProductCategories } =
      storeToRefs(categoriesStore);

    watch(allUserIEWProductCategories, () => {
      streamChecklists();
    });

    watch(allLevels, () => {
      streamChecklists();
    });

    const initializeChecklists = () => {
      streamChecklists();
    };

    const pseudoLoginStore = usePseudoLogin();
    const { isPseudoLogin } = storeToRefs(pseudoLoginStore);

    const allChecklists = ref<Checklist[]>([]);

    const streamChecklists = () => {
      checklistSubscription.value?.unsubscribe();
      checklists.value = [];
      allChecklists.value = [];

      const db = DBFactory.createDatabase();

      if (isUserAdmin.value != true || isPseudoLogin.value == true) {
        const allChecklistsStream = db.streamList({
          collection: "checklists",
          path: "/checklists",
        });

        const publicCategories = allIEWProductCategories.value.filter(
          (category) => {
            return category.isPublic == true;
          }
        );

        const publicLevelIds = allLevels.value.filter((level) => {
          return publicCategories.some((category) => {
            return category.id == level.iewGradebookProductCategoryId;
          });
        });

        allChecklistsStream.subscribe((data) => {
          allChecklists.value = data
            .flat()
            .map((d) => {
              return {
                ...d,
                path: `/checklists`,
              } as Checklist;
            })
            .filter((checklist) => {
              return publicLevelIds.some((level) => {
                return level.id == checklist.levelId;
              });
            });
        });

        const availableLevelIds = allUserLevels.value
          .filter((level) => {
            return (
              allUserIEWProductCategories.value.find(
                (category) => category.id == level.iewGradebookProductCategoryId
              ) != undefined
            );
          })
          .map((level) => level.id);
        // Loops over availableLevels in chunks of 10 and create multiple streams then combineLatest

        const chunks = useCreateChunksForArray(availableLevelIds, 10);

        const streams = chunks.map((chunk) => {
          const queryParams = [] as ModelQueryConfig;
          queryParams.push({
            field: "levelId",
            type: "where",
            operator: "in",
            value: chunk,
          });
          return db.streamList(
            {
              collection: "checklists",
              path: "/checklists",
            },
            queryParams.length > 0 ? queryParams : undefined
          );
        });

        checklistSubscription.value = combineLatest(streams).subscribe(
          (data) => {
            checklists.value = data.flat().map((d) => {
              return {
                ...d,
                path: `/checklists`,
              } as Checklist;
            });
          }
        );

        return;
      }

      checklistSubscription.value = db
        .streamList({
          collection: "checklists",
          path: "/checklists",
        })
        .pipe(
          map((data) => {
            allChecklists.value = data.flat().map((d) => {
              return {
                ...d,
                path: `/checklists`,
              } as Checklist;
            });
            checklists.value = data.map((d) => {
              return {
                ...d,
                path: `/checklists`,
              } as Checklist;
            });
          })
        )
        .subscribe();
    };

    return {
      checklists,
      allChecklists,
      initializeChecklists,
    };
  });
